import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
interface SlideFastCalculatorProps {
  onContinue: () => void;
}
function formatEuro(value: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}
interface SliderInputProps {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min: number;
  max: number;
  step?: number;
}
function SliderInput({
  label,
  value,
  onChange,
  min,
  max,
  step = 1
}: SliderInputProps) {
  return <div className="flex flex-col gap-2" data-id="element-260">
      <div className="flex items-center justify-between" data-id="element-261">
        <label className="text-sm font-medium text-slate-600" data-id="element-262">{label}</label>
        <input type="number" value={value} onChange={e => {
        const v = Number(e.target.value);
        if (!isNaN(v)) onChange(Math.max(min, Math.min(max, v)));
      }} className="w-20 text-right text-sm font-semibold text-slate-900 bg-white border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#6C2BD9]/30 focus:border-[#6C2BD9]" min={min} max={max} step={step} data-id="element-263" />
      </div>
      <input type="range" value={value} onChange={e => onChange(Number(e.target.value))} min={min} max={max} step={step} className="w-full h-1.5 bg-slate-200 rounded-full appearance-none cursor-pointer accent-[#6C2BD9] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#6C2BD9] [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-[#6C2BD9] [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:cursor-pointer" data-id="element-264" />
    </div>;
}
export function SlideFastCalculator({
  onContinue
}: SlideFastCalculatorProps) {
  const [products, setProducts] = useState(1);
  const [campaignsPerProduct, setCampaignsPerProduct] = useState(1);
  const [countries, setCountries] = useState(1);
  const [masterSnakTier, setMasterSnakTier] = useState<'none' | 'teaser' | 'trailer'>('none');
  const masterSnakCost = masterSnakTier === 'teaser' ? 2500 : masterSnakTier === 'trailer' ? 5000 : 0;
  const [campaignDuration, setCampaignDuration] = useState<3 | 6 | 12>(3);
  const MINIMUM_ORDER = 5000;
  const localizationMultiplier = masterSnakTier === 'trailer' ? 2 : 1;
  const [localizationCostBase, setLocalizationCostBase] = useState(800);
  const [monthlyInsightsFee, setMonthlyInsightsFee] = useState(100);
  const countryDeploymentCost = localizationCostBase * localizationMultiplier;
  const durationRebate = campaignDuration === 12 ? 0.1 : campaignDuration === 6 ? 0.05 : 0;
  const FOLLOW_UP_MASTER_COST = 1000;
  const calc = useMemo(() => {
    const totalCampaigns = products * campaignsPerProduct;
    const hasMaster = masterSnakCost > 0;
    const firstCampaigns = hasMaster ? products : 0;
    const followUpCampaigns = hasMaster ? Math.max(0, totalCampaigns - products) : 0;
    const annualMasterSnakCost = hasMaster ? firstCampaigns * masterSnakCost + followUpCampaigns * FOLLOW_UP_MASTER_COST : 0;
    const deploymentPerCampaign = countryDeploymentCost * countries;
    const insightsPerCampaign = monthlyInsightsFee * campaignDuration * countries;
    const rawCostFirstCampaign = masterSnakCost + deploymentPerCampaign + insightsPerCampaign;
    const rawCostFollowUp = FOLLOW_UP_MASTER_COST + deploymentPerCampaign + insightsPerCampaign;
    const rawCostRollout = deploymentPerCampaign + insightsPerCampaign;
    const rawCostPerCampaign = hasMaster ? rawCostFirstCampaign : rawCostRollout;
    const rebateAmount = rawCostPerCampaign * durationRebate;
    const costAfterRebate = rawCostPerCampaign - rebateAmount;
    const belowMinimum = hasMaster && costAfterRebate > 0 && costAfterRebate < MINIMUM_ORDER;
    const totalBeforeRebate = hasMaster ? firstCampaigns * rawCostFirstCampaign + followUpCampaigns * rawCostFollowUp : totalCampaigns * rawCostRollout;
    const totalAnnualCost = totalBeforeRebate * (1 - durationRebate);
    const costPerCampaign = totalCampaigns > 0 ? totalAnnualCost / totalCampaigns : 0;
    const annualDeploymentCost = totalCampaigns * deploymentPerCampaign;
    const annualInsightsCost = totalCampaigns * insightsPerCampaign;
    const totalDeployments = totalCampaigns * countries;
    const costPerDeployment = totalDeployments > 0 ? totalAnnualCost / totalDeployments : 0;
    return {
      totalCampaigns,
      totalDeployments,
      firstCampaigns,
      followUpCampaigns,
      rawCostPerCampaign,
      costAfterRebate,
      costPerCampaign,
      belowMinimum,
      costPerDeployment,
      totalAnnualCost,
      annualMasterSnakCost,
      annualDeploymentCost,
      annualInsightsCost
    };
  }, [products, campaignsPerProduct, countries, masterSnakCost, countryDeploymentCost, monthlyInsightsFee, campaignDuration]);
  return <div className="h-full w-full overflow-y-auto bg-white text-slate-900 font-sans" data-id="element-265">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 md:py-8" data-id="element-266">
        {/* Header */}
        <div className="mb-6" data-id="element-267">
          <div className="flex items-center gap-3 mb-1" data-id="element-268">
            <span className="text-2xl font-bold tracking-tight text-[#6C2BD9]" data-id="element-269">
              Snakker
            </span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" data-id="element-270">
            Fast Marketing<sup className="text-[0.6em] font-medium" data-id="element-271">™</sup>{' '}
            Cost Calculator
          </h1>
          <p className="text-base text-slate-500 mt-1" data-id="element-272">
            Estimate your annual campaign operating cost
          </p>
          <p className="text-xs text-slate-400 mt-0.5" data-id="element-273">
            Adjust the variables to see how your portfolio scales across brands,
            campaigns, countries, and insights.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8" data-id="element-274">
          {/* Inputs */}
          <div className="space-y-5" data-id="element-275">
            <div className="bg-[#6C2BD9]/[0.07] rounded-2xl p-5 space-y-5 border-2 border-[#6C2BD9]/20" data-id="element-276">
              <div data-id="element-277">
                <h3 className="text-xs font-bold text-[#6C2BD9] uppercase tracking-widest" data-id="element-278">
                  Unit Economics
                </h3>
                <p className="text-[11px] text-[#6C2BD9]/60 mt-0.5" data-id="element-279">
                  Set your cost baseline first — everything else scales from
                  here.
                </p>
              </div>

              {/* Master Snak Type */}
              <div className="flex flex-col gap-2" data-id="element-280">
                <label className="text-sm font-medium text-slate-600" data-id="element-281">
                  Master Snak Type
                </label>
                <div className="grid grid-cols-3 gap-2" data-id="element-282">
                  {[{
                  id: 'none',
                  title: 'Rollout Only',
                  sub: 'Masters exist'
                }, {
                  id: 'teaser',
                  title: 'Teaser',
                  sub: '€2,500'
                }, {
                  id: 'trailer',
                  title: 'Trailer',
                  sub: '€5,000'
                }].map(tier => {
                  const active = masterSnakTier === tier.id;
                  return <button key={tier.id} onClick={() => setMasterSnakTier(tier.id as any)} className={`rounded-xl border-2 p-3 text-left transition-all ${active ? 'border-[#6C2BD9] bg-[#6C2BD9]/[0.06] shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-283">
                        <p className={`text-xs font-semibold ${active ? 'text-[#6C2BD9]' : 'text-slate-700'}`} data-id="element-284">
                          {tier.title}
                        </p>
                        <p className={`mt-0.5 ${tier.id === 'none' ? `text-[11px] ${active ? 'text-[#6C2BD9]/70' : 'text-slate-400'}` : `text-base font-bold ${active ? 'text-[#6C2BD9]' : 'text-slate-900'}`}`} data-id="element-285">
                          {tier.sub}
                        </p>
                      </button>;
                })}
                </div>
              </div>

              {/* Country localization — slider */}
              <div className="flex flex-col gap-1" data-id="element-286">
                <SliderInput label="Cost per Country Localization & Set-up (€)" value={localizationCostBase} onChange={setLocalizationCostBase} min={500} max={1000} step={50} data-id="element-287" />
                <p className="text-[11px] text-slate-400" data-id="element-288">
                  Effective rate:{' '}
                  <span className="font-semibold text-slate-600" data-id="element-289">
                    €{countryDeploymentCost.toLocaleString()}
                  </span>{' '}
                  per country
                  {masterSnakTier === 'trailer' ? ' (Trailer ×2)' : ''}
                </p>
              </div>

              {/* Monthly insights — slider */}
              <div className="flex flex-col gap-1" data-id="element-290">
                <SliderInput label="Monthly Hosting & Data Insights / Country (€)" value={monthlyInsightsFee} onChange={setMonthlyInsightsFee} min={50} max={125} step={5} data-id="element-291" />
                <p className="text-[11px] text-slate-400" data-id="element-292">
                  €{monthlyInsightsFee}/mo per country × {campaignDuration}{' '}
                  months × {countries}{' '}
                  {countries === 1 ? 'country' : 'countries'}
                </p>
              </div>

              {/* Campaign duration */}
              <div className="flex flex-col gap-2" data-id="element-293">
                <label className="text-sm font-medium text-slate-600" data-id="element-294">
                  Campaign Duration
                </label>
                <div className="grid grid-cols-3 gap-2" data-id="element-295">
                  {[{
                  dur: 3,
                  label: '3 months',
                  sub: 'Standard'
                }, {
                  dur: 6,
                  label: '6 months',
                  sub: '5% rebate'
                }, {
                  dur: 12,
                  label: '12 months',
                  sub: '10% rebate'
                }].map(d => {
                  const active = campaignDuration === d.dur;
                  return <button key={d.dur} onClick={() => setCampaignDuration(d.dur as 3 | 6 | 12)} className={`rounded-lg border-2 p-2.5 text-center transition-all ${active ? 'border-[#6C2BD9] bg-[#6C2BD9]/[0.06]' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-296">
                        <p className={`text-xs font-semibold ${active ? 'text-[#6C2BD9]' : 'text-slate-700'}`} data-id="element-297">
                          {d.label}
                        </p>
                        <p className={`text-[11px] mt-0.5 ${active ? 'text-[#6C2BD9]/70' : 'text-slate-400'}`} data-id="element-298">
                          {d.sub}
                        </p>
                      </button>;
                })}
                </div>
              </div>
            </div>

            {/* Portfolio Scale */}
            <div className="bg-slate-50 rounded-2xl p-5 space-y-4 border border-slate-200" data-id="element-299">
              <div data-id="element-300">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-widest" data-id="element-301">
                  Portfolio Scale
                </h3>
                {masterSnakTier === 'none' && <p className="text-[11px] text-slate-400 mt-0.5" data-id="element-302">
                    Masters already created — calculating localization rollout
                    only.
                  </p>}
              </div>
              <SliderInput label="Number of Brands" value={products} onChange={setProducts} min={1} max={50} data-id="element-303" />
              <SliderInput label={masterSnakTier === 'none' ? 'Rollouts per Brand / Year' : 'Campaigns per Brand / Year'} value={campaignsPerProduct} onChange={setCampaignsPerProduct} min={1} max={6} data-id="element-304" />
              <SliderInput label="Number of Countries" value={countries} onChange={setCountries} min={1} max={120} data-id="element-305" />
            </div>
          </div>

          {/* Output */}
          <motion.div key={calc.totalAnnualCost} initial={{
          opacity: 0.7
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.2
        }} className="flex flex-col gap-5" data-id="element-306">
            {/* Main number */}
            <div className="bg-white rounded-2xl p-6 border-2 border-[#6C2BD9]/20 shadow-[0_0_40px_rgba(108,43,217,0.06)]" data-id="element-307">
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-2" data-id="element-308">
                Estimated Annual Cost
              </p>
              <p className="text-4xl md:text-5xl font-bold text-[#6C2BD9] tracking-tight" data-id="element-309">
                {formatEuro(calc.totalAnnualCost)}
              </p>
              <p className="text-sm text-slate-400 mt-1" data-id="element-310">per year</p>
            </div>

            {/* Volume */}
            <div className="bg-[#6C2BD9]/[0.04] rounded-2xl p-5 border border-[#6C2BD9]/10" data-id="element-311">
              <p className="text-[11px] font-semibold text-[#6C2BD9] uppercase tracking-widest mb-3" data-id="element-312">
                Campaign Volume
              </p>
              <div className="grid grid-cols-2 gap-4" data-id="element-313">
                <div data-id="element-314">
                  <p className="text-2xl font-bold text-slate-900" data-id="element-315">
                    {calc.totalCampaigns}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5" data-id="element-316">
                    master campaigns / year
                  </p>
                </div>
                <div data-id="element-317">
                  <p className="text-2xl font-bold text-[#6C2BD9]" data-id="element-318">
                    {calc.totalDeployments}
                  </p>
                  <p className="text-[11px] text-slate-500 mt-0.5" data-id="element-319">
                    localized deployments / year
                  </p>
                </div>
              </div>
              {calc.totalDeployments > 0 && <div className="mt-3 pt-3 border-t border-[#6C2BD9]/10 flex items-baseline gap-2" data-id="element-320">
                  <p className="text-xl font-bold text-[#6C2BD9]" data-id="element-321">
                    {formatEuro(calc.costPerDeployment)}
                  </p>
                  <p className="text-xs text-slate-500" data-id="element-322">
                    per localized campaign
                  </p>
                </div>}
            </div>

            {/* Minimum order warning */}
            {calc.belowMinimum && <div className="bg-amber-50 rounded-2xl p-4 border border-amber-200" data-id="element-323">
                <p className="text-sm font-medium text-amber-800" data-id="element-324">
                  Minimum order per campaign: {formatEuro(MINIMUM_ORDER)}
                </p>
                <p className="text-xs text-amber-600 mt-1" data-id="element-325">
                  Your configured cost is {formatEuro(calc.rawCostPerCampaign)}{' '}
                  per campaign. Add more countries, extend duration, or increase
                  unit costs to reach the minimum.
                </p>
              </div>}

            {/* Breakdown */}
            <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200" data-id="element-326">
              <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-widest mb-3" data-id="element-327">
                Cost Breakdown
              </p>
              <div className="space-y-2" data-id="element-328">
                <div className="flex justify-between items-center" data-id="element-329">
                  <span className="text-sm text-slate-600" data-id="element-330">
                    Avg. cost per campaign
                  </span>
                  <span className="text-sm font-semibold text-slate-900" data-id="element-331">
                    {formatEuro(calc.costPerCampaign)}
                  </span>
                </div>
                <div className="h-px bg-slate-200 my-1" data-id="element-332" />
                {calc.firstCampaigns > 0 && <div className="flex justify-between items-center" data-id="element-333">
                    <span className="text-sm text-slate-500" data-id="element-334">
                      New masters ({calc.firstCampaigns} ×{' '}
                      {formatEuro(masterSnakCost)})
                    </span>
                    <span className="text-sm text-slate-700" data-id="element-335">
                      {formatEuro(calc.firstCampaigns * masterSnakCost)}
                    </span>
                  </div>}
                {calc.followUpCampaigns > 0 && <div className="flex justify-between items-center" data-id="element-336">
                    <span className="text-sm text-slate-500" data-id="element-337">
                      Follow-up campaigns ({calc.followUpCampaigns} × €1,000)
                    </span>
                    <span className="text-sm text-slate-700" data-id="element-338">
                      {formatEuro(calc.followUpCampaigns * FOLLOW_UP_MASTER_COST)}
                    </span>
                  </div>}
                <div className="flex justify-between items-center" data-id="element-339">
                  <span className="text-sm text-slate-500" data-id="element-340">
                    Localization & set-up
                  </span>
                  <span className="text-sm text-slate-700" data-id="element-341">
                    {formatEuro(calc.annualDeploymentCost)}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-342">
                  <span className="text-sm text-slate-500" data-id="element-343">
                    Hosting & data insights ({campaignDuration}mo)
                  </span>
                  <span className="text-sm text-slate-700" data-id="element-344">
                    {formatEuro(calc.annualInsightsCost)}
                  </span>
                </div>
                {durationRebate > 0 && <div className="flex justify-between items-center" data-id="element-345">
                    <span className="text-sm text-[#6C2BD9] font-medium" data-id="element-346">
                      Duration rebate ({Math.round(durationRebate * 100)}%)
                    </span>
                    <span className="text-sm text-[#6C2BD9] font-medium" data-id="element-347">
                      −
                      {formatEuro(calc.totalAnnualCost / (1 - durationRebate) - calc.totalAnnualCost)}
                    </span>
                  </div>}
              </div>
            </div>

            {/* Strategic callout */}
            <div className="bg-[#6C2BD9]/5 rounded-2xl p-4 border border-[#6C2BD9]/10" data-id="element-348">
              <p className="text-sm font-medium text-[#6C2BD9] leading-relaxed" data-id="element-349">
                This is not software pricing. This is the annual cost of a fully
                managed campaign operating model across your portfolio.
              </p>
            </div>

            <p className="text-[11px] text-slate-400 leading-relaxed px-1" data-id="element-350">
              As you scale across more products, campaigns, or countries, cost
              grows predictably — while execution stays centralized.
            </p>
          </motion.div>
        </div>

        {/* Continue button */}
        <div className="mt-6 flex justify-end" data-id="element-351">
          <Button onClick={onContinue} data-id="element-352">Thank You</Button>
        </div>
      </div>
    </div>;
}